import React, { useCallback, useEffect, useRef } from 'react';

import type { ComponentGroups, PersonalisationSlot } from '../../context/personalisation-typedefs';
import { usePersonalisationContext } from '../../context/PersonalisationContext';
import PersonalisationSlotHeader from '../PersonalisationSlotHeader';
import PersonalisationAlphanumericSlot from './PersonalisationAlphanumericSlot.jsx';
import { isRequiredSlot, usePairedSlotViewModel } from './usePairedSlotViewModel';

import stepStyles from './personalisation-step.module.css';
import gridStyles from './personalisation-grid.module.css';
import styles from './personalisation-paired.module.css';

interface PersonalisationPairedSlotProps {
  slots: PersonalisationSlot[];
  componentGroups: ComponentGroups | null;
}

/**
 * PersonalisationPairedSlot — renders slots when the step slotMode is PAIRED.
 *
 * Each COMPONENT slot is joined with the ALPHANUMERIC slots that share its orderIndex — an
 * optional year and an optional moment — and shown as one row: an "Add Stone" button while
 * empty, then a card naming the stone and its moment, with Edit and Remove. Opening a row swaps
 * the row list for the picker, a full-screen view under the journey header: the stone grid, then
 * (once a stone is chosen) the year and moment inputs and a live preview of the card line, over
 * a sticky Cancel / Continue bar. While open, the picker is the context's `stepSubview`, so the
 * modal titles the header "Stone n of m", hides the visualiser and the footer, and sends Escape
 * here rather than closing the journey. Empty rows put nothing on the piece preview.
 *
 * @param props - Component props.
 * @param props.slots - Every slot on the step.
 * @param props.componentGroups - Resolved component groups from the context.
 * @returns The row list, or the picker when a row is open.
 */
export default function PersonalisationPairedSlot({
  slots,
  componentGroups
}: PersonalisationPairedSlotProps): React.JSX.Element {
  const { setStepSubview } = usePersonalisationContext();
  const {
    rows,
    headerSlot,
    isSheetOpen,
    openRowIndex,
    sheetTitle,
    sheetSubtitle,
    stoneChoices,
    stoneColumns,
    yearSlotConfig,
    yearLabel,
    momentSlotConfig,
    momentLabel,
    pendingVariantId,
    pendingYearValue,
    pendingMomentValue,
    yearError,
    momentError,
    liveLineText,
    canSave,
    openRow,
    cancelSheet,
    selectStone,
    changeYear,
    blurYear,
    blurMoment,
    changeMoment,
    saveRow,
    removeRow
  } = usePairedSlotViewModel({ slots, componentGroups });

  const pickerRef = useRef<HTMLElement | null>(null);
  const yearErrorId = `paired-picker-year-error-${openRowIndex}`;
  const momentErrorId = `paired-picker-moment-error-${openRowIndex}`;
  // The control that opens each row: its "Add Stone" button while empty, its Edit button once
  // filled. Focus goes back to it when the picker closes.
  const rowControlRefs = useRef(new Map<number, HTMLButtonElement>());
  const rowIndexToRestoreRef = useRef<number | null>(null);
  // The row whose Remove was pressed: its Remove button unmounts, so focus moves to the row's
  // "Add Stone" button once the cleared selection has re-rendered it as empty.
  const rowIndexToFocusAfterRemoveRef = useRef<number | null>(null);

  const registerRowControl = useCallback(
    (rowIndex: number) => (element: HTMLButtonElement | null) => {
      if (element) {
        rowControlRefs.current.set(rowIndex, element);
      } else {
        rowControlRefs.current.delete(rowIndex);
      }
    },
    []
  );

  const handleOpenRow = useCallback(
    (rowIndex: number) => {
      rowIndexToRestoreRef.current = rowIndex;
      openRow(rowIndex);
    },
    [openRow]
  );

  const handleRemoveRow = useCallback(
    (rowIndex: number) => {
      rowIndexToFocusAfterRemoveRef.current = rowIndex;
      removeRow(rowIndex);
    },
    [removeRow]
  );

  useEffect(() => {
    const rowIndex = rowIndexToFocusAfterRemoveRef.current;
    if (rowIndex === null) return;
    // Still filled: the cleared selection has not come back from the context yet.
    if (rows.find((row) => row.index === rowIndex)?.isFilled) return;
    rowIndexToFocusAfterRemoveRef.current = null;
    rowControlRefs.current.get(rowIndex)?.focus();
  }, [rows]);

  // While open, the picker is the step's sub-view; closing it or leaving the step clears it.
  // `cancelSheet` and the setter are stable, so this runs once per open.
  useEffect(() => {
    if (!isSheetOpen) return undefined;
    setStepSubview({ title: sheetTitle, onCancel: cancelSheet });
    return () => setStepSubview(null);
  }, [isSheetOpen, sheetTitle, cancelSheet, setStepSubview]);

  // Moves focus to the chosen stone (else the first one on offer) on open, and back to the row's
  // control on close. The modal scrolls the content area to the top as the picker opens, so the
  // focus must not scroll it again.
  useEffect(() => {
    if (isSheetOpen) {
      const picker = pickerRef.current;
      const tile =
        picker?.querySelector<HTMLElement>('button[aria-pressed="true"]') ??
        picker?.querySelector<HTMLElement>('button[aria-pressed]:not(:disabled)');
      tile?.focus({ preventScroll: true });
      return;
    }

    const rowIndexToRestore = rowIndexToRestoreRef.current;
    if (rowIndexToRestore === null) return;
    rowControlRefs.current.get(rowIndexToRestore)?.focus();
    rowIndexToRestoreRef.current = null;
  }, [isSheetOpen]);

  // Escape closes the picker. The modal also turns the dialog's close request into a cancel while
  // the picker is open, which covers focus outside it; this handles focus inside it first.
  const handlePickerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Escape') return;
      // The whole journey renders inside a modal <dialog closedby="any">, so the default Escape
      // close request must be suppressed or it would close the journey instead of the picker.
      event.preventDefault();
      event.stopPropagation();
      cancelSheet();
    },
    [cancelSheet]
  );

  if (isSheetOpen) {
    return (
      <div className={stepStyles['personalisation__step'] + ' ' + styles['personalisation__paired-step--picker']}>
        <section
          ref={pickerRef}
          aria-label={sheetTitle}
          className={styles['personalisation__paired-picker']}
          onKeyDown={handlePickerKeyDown}
        >
          <p className="body-base">{sheetSubtitle}</p>

          <fieldset className={styles['personalisation__paired-picker-stones']}>
            <legend className="visually-hidden">Meaningful stone</legend>
            <ul
              className={
                gridStyles['personalisation__selection-grid'] +
                ' ' +
                gridStyles[`personalisation__selection-grid--${stoneColumns}-column`] +
                ' ' +
                styles['personalisation__paired-stone-grid']
              }
              role="list"
            >
              {stoneChoices.map((choice) => (
                <li key={choice.variantId}>
                  <button
                    type="button"
                    className={
                      styles['personalisation__paired-stone'] +
                      (choice.isSelected ? ' ' + styles['personalisation__paired-stone--selected'] : '') +
                      ' button-reset'
                    }
                    aria-pressed={choice.isSelected}
                    // Two lines read as "January, Garnet"; a one-line title names the tile itself.
                    aria-label={choice.titleLines.length > 1 ? choice.accessibleName : undefined}
                    disabled={!choice.isAvailable}
                    onClick={() => selectStone(choice.variantId)}
                  >
                    <span className={styles['personalisation__paired-stone-media']} aria-hidden="true">
                      {choice.imageUrl ? (
                        <img src={choice.imageUrl} alt="" loading="lazy" />
                      ) : (
                        <span className={styles['personalisation__paired-stone-swatch']} />
                      )}
                    </span>
                    <span className={styles['personalisation__paired-stone-title'] + ' body-base'}>
                      {choice.titleLines.map((line) => (
                        <span key={line} className={styles['personalisation__paired-stone-line']}>
                          {line}
                        </span>
                      ))}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </fieldset>

          <div className={styles['personalisation__paired-picker-bottom']}>
            {pendingVariantId && (yearSlotConfig || momentSlotConfig) && (
              <div className={styles['personalisation__paired-moment']}>
                {yearSlotConfig && (
                  // React's onBlur bubbles (it listens for focusout), so this wrapper hears the year
                  // input lose focus without the shared input needing an onBlur prop. A half-typed
                  // year shows its error then, not on every keystroke.
                  <div className={styles['personalisation__paired-moment-field']} onBlur={blurYear}>
                    {/* As with the moment, the input's own header and label are blanked; this is its one label. */}
                    <label
                      htmlFor={`input__text__${yearSlotConfig.id}`}
                      className={'body-base' + (isRequiredSlot(yearSlotConfig) ? ' field__label--required' : '')}
                    >
                      {yearLabel}
                    </label>
                    <PersonalisationAlphanumericSlot
                      slot={yearSlotConfig.id}
                      value={yearSlotConfig}
                      inputValue={pendingYearValue}
                      suppressFontSelection
                      onChange={(_slotId: string, newValue: unknown) => changeYear(newValue)}
                      ariaInvalid={yearError !== null}
                      describedBy={yearError ? yearErrorId : undefined}
                    />
                    {yearError && (
                      <p
                        id={yearErrorId}
                        role="alert"
                        className={styles['personalisation__paired-moment-error'] + ' body-xs'}
                      >
                        {yearError}
                      </p>
                    )}
                  </div>
                )}

                {momentSlotConfig && (
                  // As with the year, the wrapper hears the moment input lose focus.
                  <div className={styles['personalisation__paired-moment-field']} onBlur={blurMoment}>
                    {/* The input's own header and label are blanked in its config; this is its one label. */}
                    <label
                      htmlFor={`input__text__${momentSlotConfig.id}`}
                      className={'body-base' + (isRequiredSlot(momentSlotConfig) ? ' field__label--required' : '')}
                    >
                      {momentLabel}
                    </label>
                    <PersonalisationAlphanumericSlot
                      slot={momentSlotConfig.id}
                      value={momentSlotConfig}
                      inputValue={pendingMomentValue}
                      suppressFontSelection
                      onChange={(_slotId: string, newValue: unknown) => changeMoment(newValue)}
                      ariaInvalid={momentError !== null}
                      describedBy={momentError ? momentErrorId : undefined}
                    />
                    {momentError && (
                      <p
                        id={momentErrorId}
                        role="alert"
                        className={styles['personalisation__paired-moment-error'] + ' body-xs'}
                      >
                        {momentError}
                      </p>
                    )}
                  </div>
                )}

                <p aria-live="polite" className="body-sm">
                  {liveLineText}
                </p>
              </div>
            )}

            <div className={styles['personalisation__paired-picker-actions']}>
              <button type="button" className="button button--secondary button--black" onClick={cancelSheet}>
                Cancel
              </button>
              <button
                type="button"
                className="button button--primary button--orange"
                onClick={saveRow}
                disabled={!canSave}
              >
                Continue
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={stepStyles['personalisation__step']}>
      {headerSlot && <PersonalisationSlotHeader slot={headerSlot} />}
      <ul className={styles['personalisation__paired-rows']} role="list">
        {rows.map((row) => (
          <li key={row.componentSlot.id}>
            {row.isFilled ? (
              <div className={styles['personalisation__paired-card']}>
                <span className={styles['personalisation__paired-row-media']} aria-hidden="true">
                  {row.variantImageUrl ? (
                    <img src={row.variantImageUrl} alt="" loading="lazy" />
                  ) : (
                    <span className={styles['personalisation__paired-row-swatch']} />
                  )}
                </span>
                <div className={styles['personalisation__paired-row-text']}>
                  <p className={styles['personalisation__paired-row-title'] + ' body-base'}>{row.primaryLabel}</p>
                  <p className={styles['personalisation__paired-row-subtitle'] + ' body-sm'}>{row.secondaryLabel}</p>
                  <div className={styles['personalisation__paired-card-actions']}>
                    <button
                      type="button"
                      ref={registerRowControl(row.index)}
                      className={styles['personalisation__paired-card-action'] + ' body-sm button-reset'}
                      onClick={() => handleOpenRow(row.index)}
                      disabled={row.componentSlot.disabled}
                      aria-label={row.editLabel}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={styles['personalisation__paired-card-action'] + ' body-sm button-reset'}
                      onClick={() => handleRemoveRow(row.index)}
                      aria-label={row.removeLabel}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <span className={styles['personalisation__paired-check']} aria-hidden="true" />
              </div>
            ) : (
              <button
                type="button"
                ref={registerRowControl(row.index)}
                className={styles['personalisation__paired-row-button'] + ' button-reset'}
                onClick={() => handleOpenRow(row.index)}
                disabled={row.componentSlot.disabled}
              >
                <span className={styles['personalisation__paired-row-add']} aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2.5V17.5M2.5 10H17.5" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" />
                  </svg>
                </span>
                <span className={styles['personalisation__paired-row-text']}>
                  <span className={styles['personalisation__paired-row-title'] + ' body-base'}>{row.primaryLabel}</span>{' '}
                  <span className={styles['personalisation__paired-row-subtitle'] + ' body-sm'}>
                    {row.secondaryLabel}
                  </span>
                </span>
                <span className={styles['personalisation__paired-radio']} aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
